import ChartView from 'echarts/lib/view/Chart';
import type GlobalModel from 'echarts/lib/model/Global';
import type ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import * as graphic from 'echarts/lib/util/graphic';
import { normalizeSymbolSize } from 'echarts/lib/util/symbol';
import SeriesData from 'echarts/lib/data/SeriesData';
import { VectorArray } from 'zrender/lib/core/vector.js';

import { setStatesStylesFromModel, toggleHoverEmphasis } from 'echarts/lib/util/states';
import * as zrUtil from 'zrender/lib/core/util.js';
import * as symbolUtil from 'echarts/lib/util/symbol';
import { ColorString, ViewRootGroup } from 'echarts/lib/util/types';
import { setLabelStyle, getLabelStatesModels } from 'echarts/lib/label/labelStyle';
import { saveOldStyle } from 'echarts/lib/animation/basicTransition';
import { DvRadarSeriesDataItemOption, DvRadarSeriesModelClazz } from './radarSeriesModel';

export type DvRadarSymbol = ReturnType<typeof symbolUtil.createSymbol> & {
  __dimIdx: number;
};

const transparentColor = 'rgba(0,0,0,0)';

// 常量定义
const CHILD_INDICES = {
  POLYLINE: 0,
  POLYGON: 1,
  SYMBOL_GROUP: 2,
  HIGHLIGHT_POLYGON: 3,
  BACKGROUND_POLYGON: 4,
  BACKGROUND_LINE: 5
} as const;

const SYMBOL_SIZE = 2;
const DEGREES_TO_RADIANS = 180;
const CIRCLE_RADIANS = Math.PI * 2;
const SECTOR_Z_LEVEL = 4;
const SECTOR_SHAPE_Z_LEVEL = 10;
const OPACITY = 0.4;
const SYMBOL_Z_INDEX = 10;
const HALF_DIVISOR = 2;
const NEGATIVE_TWO = -2;
const THREE = 3;
const HIGHLIGHT_Z_INDEX = 5;
const MASK_Z_INDEX = 4;

function getInitialPoints(seriesModel: DvRadarSeriesModelClazz, points: number[][]): number[][] {
  const radarSys = seriesModel.coordinateSystem;
  return zrUtil.map(points, function () {
    return [radarSys.cx, radarSys.cy];
  });
}

function createSymbol(
  data: SeriesData<DvRadarSeriesModelClazz>,
  idx: number
): DvRadarSymbol | undefined {
  const symbolType = data.getItemVisual(idx, 'symbol') || 'circle';
  if (symbolType === 'none') {
    return undefined;
  }
  const symbolSize = normalizeSymbolSize(data.getItemVisual(idx, 'symbolSize'));
  const symbolPath = symbolUtil.createSymbol(symbolType, -1, -1, SYMBOL_SIZE, SYMBOL_SIZE);
  const symbolRotate = data.getItemVisual(idx, 'symbolRotate') || 0;
  symbolPath.attr({
    style: {
      strokeNoScale: false
    },
    z2: SYMBOL_Z_INDEX,
    scaleX: symbolSize[0] / HALF_DIVISOR,
    scaleY: symbolSize[1] / HALF_DIVISOR,
    rotation: (symbolRotate * Math.PI) / DEGREES_TO_RADIANS || 0
  });
  return symbolPath as DvRadarSymbol;
}

function updateSymbols(
  seriesModel: DvRadarSeriesModelClazz,
  oldPoints: VectorArray[],
  newPoints: VectorArray[],
  symbolGroup: graphic.Group,
  data: SeriesData<DvRadarSeriesModelClazz>,
  idx: number,
  isInit?: boolean
) {
  // Simply rerender all
  symbolGroup.removeAll();
  for (let i = 0; i < newPoints.length - 1; i++) {
    const symbolPath = createSymbol(data, idx);
    if (symbolPath) {
      symbolPath.__dimIdx = i;
      if (oldPoints[i]) {
        symbolPath.setPosition(oldPoints[i]);
        graphic[isInit ? 'initProps' : 'updateProps'](
          symbolPath,
          {
            x: newPoints[i][0],
            y: newPoints[i][1]
          },
          seriesModel,
          idx
        );
      } else {
        symbolPath.setPosition(newPoints[i]);
      }
      symbolGroup.add(symbolPath);
    }
  }
}

// 增加蒙层
function addMask(group: ViewRootGroup, seriesModel: DvRadarSeriesModelClazz, elId: number) {
  const { highlight } = seriesModel.option;
  if (!highlight || !highlight.show) {
    return;
  }
  const x = seriesModel.coordinateSystem.cx;
  const y = seriesModel.coordinateSystem.cy;
  const { r } = seriesModel.coordinateSystem;
  const mask = new graphic.Circle({
    shape: {
      cx: x,
      cy: y,
      r
    },
    style: {
      fill: 'none'
    },
    z2: MASK_Z_INDEX
  });
  const maskGroup = new graphic.Group();
  const data = seriesModel.getData();
  maskGroup.add(mask);
  data.setItemGraphicEl(elId + 1, mask);
  group.add(maskGroup);
}

// 添加扇形选择器
function addSectorSelector(
  radarPointer: graphic.Line,
  group: ViewRootGroup,
  seriesModel: DvRadarSeriesModelClazz,
  indicatorGroup: graphic.Group,
  elId: number,
  api: ExtensionAPI
) {
  const { sectorSelector } = seriesModel.option;

  if (!sectorSelector) {
    return;
  }

  if (sectorSelector.show === undefined) {
    sectorSelector.show = true;
  }

  if (sectorSelector.show === true) {
    // 确定轴的数量
    const dimensionNumber = seriesModel.coordinateSystem.dimensions.length;
    // 计算每个扇形的角度
    const angleRange = CIRCLE_RADIANS / dimensionNumber;

    let startAngle = (NEGATIVE_TWO * Math.PI) / THREE;
    let endAngel = startAngle + angleRange;
    let sectorStartAngle = (-1 / HALF_DIVISOR) * Math.PI - (1 / HALF_DIVISOR) * angleRange;
    const x = seriesModel.coordinateSystem.cx;
    const y = seriesModel.coordinateSystem.cy;
    const { r } = seriesModel.coordinateSystem;

    if (sectorSelector.lineStyle) {
      // 绘制axisPointer线条
      radarPointer = new graphic.Line({
        name: 'radarPointer',
        shape: {
          x1: x,
          y1: y,
          x2: x,
          y2: y
        },
        style: sectorSelector.lineStyle,
        zlevel: SECTOR_Z_LEVEL
      });
      // 添加新的线条
      indicatorGroup.add(radarPointer);
    }

    // 给每个轴绘制扇形
    for (let i = 0; i < dimensionNumber; i++) {
      const sectorEndAngle = sectorStartAngle + angleRange;
      const sector = new graphic.Sector({
        hover_id: i,
        shape: {
          cx: x,
          cy: y,
          r,
          // 内半径，要绘制扇形而非圆环，设置为0
          r0: 0,
          startAngle: sectorStartAngle,
          endAngle: sectorEndAngle
        },
        style: {
          // 扇形的初始填充颜色为透明
          fill: transparentColor
        },
        z2: SECTOR_SHAPE_Z_LEVEL
      });

      startAngle = endAngel;
      endAngel = startAngle + CIRCLE_RADIANS / dimensionNumber;
      sectorStartAngle = sectorEndAngle;
      const linePointer = radarPointer;
      // 为扇形添加鼠标悬停事件代理
      sector.on('mouseover', event => {
        api.dispatchAction({
          type: 'radarMouseOverSector',
          event,
          line: linePointer,
          group
        });
      });

      // 为扇形添加鼠标移出事件代理
      sector.on('mouseout', event => {
        api.dispatchAction({
          type: 'radarMouseOutSector',
          event,
          line: linePointer,
          group
        });
      });
      const data = seriesModel.getData();
      data.setItemGraphicEl(elId + i, sector);
      indicatorGroup.add(sector);
    }
    group.add(indicatorGroup);
  }
}

export type DvRadarViewClazz = DvRadarView & ChartView;

class DvRadarView extends ChartView {
  static readonly type = 'dvRadar';

  type = DvRadarView.type;

  private _data: SeriesData<DvRadarViewClazz>;

  _exampleGroup: graphic.Group;

  _activeIndex: number | undefined;

  public radarPointer: graphic.Line;

  init() {
    // TODO document why this method 'init' is empty
  }

  private createRadarPolygons(
    seriesModel: DvRadarSeriesModelClazz,
    data: SeriesData<DvRadarViewClazz>
  ) {
    return function (idx: number) {
      const points = data.getItemLayout(idx);
      const itemModel = data.getItemModel<DvRadarSeriesDataItemOption>(idx);
      const smoothValue = itemModel.option.smooth ?? seriesModel.option.smooth ?? 0;

      if (!points) {
        return;
      }

      const polygon = new graphic.Polygon();
      const polygonLine = new graphic.Polygon();
      const highlightPolygon = new graphic.Polygon();
      const backgroundPolygon = new graphic.Polygon();
      const backgroundLine = new graphic.Polygon();

      const target = {
        shape: {
          points,
          smooth: smoothValue
        }
      };

      const initialPoints = getInitialPoints(seriesModel, points);
      polygon.shape.points = initialPoints;
      polygonLine.shape.points = initialPoints;
      highlightPolygon.shape.points = initialPoints;
      backgroundPolygon.shape.points = initialPoints;
      backgroundLine.shape.points = initialPoints;

      graphic.initProps(
        polygon,
        {
          ...target,
          style: {
            fill: 'none',
            stroke: '#000'
          }
        },
        seriesModel,
        idx
      );
      graphic.initProps(
        polygonLine,
        {
          ...target,
          style: {
            fill: 'none'
          },
          z2: HIGHLIGHT_Z_INDEX
        },
        seriesModel,
        idx
      );
      graphic.initProps(
        highlightPolygon,
        {
          shape: {
            ...target.shape
          },
          style: {
            fill: 'none'
          },
          z2: HIGHLIGHT_Z_INDEX
        },
        seriesModel,
        idx
      );
      graphic.initProps(
        backgroundPolygon,
        {
          ...target,
          style: {
            fill: 'none'
          },
          z2: HIGHLIGHT_Z_INDEX
        },
        seriesModel,
        idx
      );
      graphic.initProps(
        backgroundLine,
        {
          ...target,
          style: {
            fill: 'none',
            stroke: 'none'
          },
          z2: HIGHLIGHT_Z_INDEX
        },
        seriesModel,
        idx
      );

      const itemGroup = new graphic.Group();
      const symbolGroup = new graphic.Group();
      itemGroup.add(polygonLine);
      itemGroup.add(polygon);
      itemGroup.add(symbolGroup);
      itemGroup.add(highlightPolygon);
      itemGroup.add(backgroundPolygon);
      itemGroup.add(backgroundLine);

      updateSymbols(
        seriesModel,
        polygonLine.shape.points,
        [...points, points[0]],
        symbolGroup,
        data,
        idx,
        true
      );
      data.setItemGraphicEl(idx, itemGroup);
    };
  }

  private updateRadarPolygons(
    seriesModel: DvRadarSeriesModelClazz,
    data: SeriesData<DvRadarViewClazz>,
    oldData: SeriesData<DvRadarViewClazz>
  ) {
    return function (newIdx: number, oldIdx: number) {
      const itemGroup = oldData.getItemGraphicEl(oldIdx) as graphic.Group;

      const polyline = itemGroup.childAt(CHILD_INDICES.POLYLINE) as graphic.Polyline;
      const polygon = itemGroup.childAt(CHILD_INDICES.POLYGON) as graphic.Polygon;
      const symbolGroup = itemGroup.childAt(CHILD_INDICES.SYMBOL_GROUP) as graphic.Group;
      const highlightPolygon = itemGroup.childAt(
        CHILD_INDICES.HIGHLIGHT_POLYGON
      ) as graphic.Polygon;
      const backgroundPolygon = itemGroup.childAt(
        CHILD_INDICES.BACKGROUND_POLYGON
      ) as graphic.Polygon;
      const backgroundLine = itemGroup.childAt(CHILD_INDICES.BACKGROUND_LINE) as graphic.Polygon;

      const points = data.getItemLayout(newIdx);
      const itemModel = data.getItemModel<DvRadarSeriesDataItemOption>(newIdx);
      const smoothValue = itemModel.option.smooth ?? seriesModel.option.smooth ?? 0;

      const target = {
        shape: {
          points,
          smooth: smoothValue
        }
      };

      if (!target.shape.points) {
        return;
      }

      updateSymbols(
        seriesModel,
        polyline.shape.points,
        [...target.shape.points, target.shape.points[0]],
        symbolGroup,
        data,
        newIdx,
        false
      );

      saveOldStyle(polygon);
      saveOldStyle(polyline);
      saveOldStyle(highlightPolygon);
      saveOldStyle(backgroundPolygon);
      saveOldStyle(backgroundLine);

      graphic.updateProps(polygon, target, seriesModel);
      graphic.updateProps(polyline, target, seriesModel);
      graphic.updateProps(highlightPolygon, target, seriesModel);
      graphic.updateProps(backgroundPolygon, target, seriesModel);
      graphic.updateProps(backgroundLine, target, seriesModel);

      data.setItemGraphicEl(newIdx, itemGroup);
    };
  }

  private setupItemStyles(
    seriesModel: DvRadarSeriesModelClazz,
    data: SeriesData<DvRadarViewClazz>,
    group: ViewRootGroup
  ) {
    data.eachItemGraphicEl(function (itemGroup: graphic.Group, idx) {
      const itemModel = data.getItemModel<DvRadarSeriesDataItemOption>(idx);
      const polyline = itemGroup.childAt(CHILD_INDICES.POLYLINE) as graphic.Polyline;
      const polygon = itemGroup.childAt(CHILD_INDICES.POLYGON) as graphic.Polygon;
      const symbolGroup = itemGroup.childAt(CHILD_INDICES.SYMBOL_GROUP) as graphic.Group;
      const highlightPolygon = itemGroup.childAt(
        CHILD_INDICES.HIGHLIGHT_POLYGON
      ) as graphic.Polygon;

      const itemStyle = data.getItemVisual(idx, 'style');
      const color = itemStyle.fill;
      const { highlight, lineStyle } = seriesModel.option;

      group.add(itemGroup);

      polyline.useStyle(
        zrUtil.defaults(
          { ...(highlight?.lineStyle || {}), fill: 'none' },
          {
            stroke: color,
            ...highlight?.lineStyle,
            fill: 'none',
            lineWidth: highlight?.lineStyle?.width ?? lineStyle.width
          }
        )
      );

      if (highlight?.show) {
        highlightPolygon.useStyle(
          zrUtil.defaults(itemModel.getModel('lineStyle').getLineStyle(), {
            fill: color,
            opacity: OPACITY,
            decal: itemStyle.decal,
            stroke: color,
            strokeNoScale: false,
            ...highlight?.areaStyle
          })
        );
      }

      setStatesStylesFromModel(polyline, itemModel, 'lineStyle');
      setStatesStylesFromModel(polygon, itemModel, 'areaStyle');

      const areaStyleModel = itemModel.getModel('areaStyle');
      const polygonIgnore = areaStyleModel.isEmpty() && areaStyleModel.parentModel.isEmpty();

      polygon.ignore = polygonIgnore;

      zrUtil.each(['emphasis', 'select', 'blur'] as const, function (stateName) {
        const stateModel = itemModel.getModel([stateName, 'areaStyle']);
        const stateIgnore = stateModel.isEmpty() && stateModel.parentModel.isEmpty();
        polygon.ensureState(stateName).ignore = stateIgnore && polygonIgnore;
      });

      polygon.useStyle(
        zrUtil.defaults(areaStyleModel.getAreaStyle(), {
          fill: color,
          opacity: OPACITY,
          decal: itemStyle.decal,
          stroke: color,
          strokeNoScale: false
        })
      );

      const emphasisModel = itemModel.getModel('emphasis');
      const itemHoverStyle = emphasisModel.getModel('itemStyle').getItemStyle();

      symbolGroup.eachChild(function (symbolPath: DvRadarSymbol) {
        symbolPath.useStyle(itemStyle);
        symbolPath.setColor(color);
        symbolPath.style.strokeNoScale = true;

        const pathEmphasisState = symbolPath.ensureState('emphasis');
        pathEmphasisState.style = zrUtil.clone(itemHoverStyle);
        let defaultText = data.getStore().get(data.getDimensionIndex(symbolPath.__dimIdx), idx);

        if (defaultText == null || Number.isNaN(defaultText as number)) {
          defaultText = '';
        }

        setLabelStyle(symbolPath, getLabelStatesModels(itemModel), {
          labelFetcher: data.hostModel,
          labelDataIndex: idx,
          labelDimIndex: symbolPath.__dimIdx,
          defaultText: defaultText as string,
          inheritColor: color as ColorString,
          defaultOpacity: itemStyle.opacity
        });
      });

      toggleHoverEmphasis(
        itemGroup,
        emphasisModel.get('focus'),
        emphasisModel.get('blurScope'),
        emphasisModel.get('disabled')
      );
    });
  }

  render(
    this: DvRadarViewClazz,
    seriesModel: DvRadarSeriesModelClazz,
    ecModel: GlobalModel,
    api: ExtensionAPI
  ) {
    const { group } = this;
    const data = seriesModel.getData();
    const oldData = this._data;
    const indicatorGroup = new graphic.Group();

    // 处理数据差异
    data
      .diff(oldData)
      .add(this.createRadarPolygons(seriesModel, data))
      .update(this.updateRadarPolygons(seriesModel, data, oldData))
      .remove(function (idx) {
        group.remove(oldData.getItemGraphicEl(idx));
      })
      .execute();

    // 设置样式
    this.setupItemStyles(seriesModel, data, group);

    this._data = data;

    // 计算元素ID并添加扇形选择器
    let elId = 0;
    data.eachItemGraphicEl(function () {
      elId += 1;
    });

    addSectorSelector(this.radarPointer, group, seriesModel, indicatorGroup, elId, api);
    addMask(group, seriesModel, elId);
  }

  remove(this: DvRadarViewClazz) {
    this.group.removeAll();
    this._data = null;
    this.radarPointer = null;
  }
}

export default DvRadarView;
